import React, { MouseEventHandler } from 'react';

import {
  Button,
  ButtonVariant,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextFieldType,
} from '@cfa/react-components';
import { Colors } from '@cfa/react-components/dist/cjs/internal/types';

import { cn } from '@/utils/tailwind';

interface ConfirmationModalProps {
  bodyText: string;
  bodyTextColor?: string;
  children?: React.ReactNode;
  className?: string;
  headerText: string;
  isDisabled?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  onClose?: (e: React.ChangeEvent<TextFieldType>) => void;
  overlayProps?: Record<string, unknown>;
  primaryButtonColor?: Colors;
  primaryButtonHandler?: MouseEventHandler<HTMLButtonElement>;
  primaryButtonText: string;
  primaryButtonVariant?: ButtonVariant;
  secondaryButtonHandler?: MouseEventHandler<HTMLButtonElement>;
  secondaryButtonColor?: Colors;
  secondaryButtonText?: string;
  showCloseButton?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  bodyText,
  bodyTextColor,
  children,
  className,
  headerText,
  isDisabled,
  isError,
  isLoading,
  isOpen,
  onClose,
  overlayProps,
  primaryButtonColor = 'secondary',
  primaryButtonHandler,
  primaryButtonText,
  primaryButtonVariant = 'filled',
  secondaryButtonColor = 'secondary',
  secondaryButtonHandler,
  secondaryButtonText,
  showCloseButton = true,
}) => (
  <Modal
    className={className}
    data-testid="ConfirmationModal"
    onClose={onClose as () => void}
    overlayProps={overlayProps}
    show={isOpen}
    size="md"
  >
    <>
      <ModalHeader
        className="text-24!"
        data-testid="ConfirmationModalHeaderText"
        showCloseButton={showCloseButton}
      >
        {headerText}
      </ModalHeader>
      {/** @ts-expect-error overload error but we only need the props below */}
      <ModalBody
        className={cn(
          bodyTextColor ? 'text-feedback-success-base!' : 'text-inherit!',
        )}
        dangerouslySetInnerHTML={{ __html: bodyText }}
      />
      {children && <div className="mx-auto">{children}</div>}
      <ModalFooter>
        {secondaryButtonHandler && secondaryButtonText && (
          <Button
            color={secondaryButtonColor}
            data-testid="ConfirmationPopUpSecondaryButton"
            onClick={secondaryButtonHandler}
            variant="outlined"
          >
            {secondaryButtonText}
          </Button>
        )}
        <Button
          className={
            // Is this one of two buttons:
            cn(!(secondaryButtonHandler && secondaryButtonText)) && 'sm:flex-1'
          }
          color={primaryButtonColor}
          data-testid="ConfirmationPopUpPrimaryButton"
          disabled={isDisabled}
          loading={isLoading}
          onClick={primaryButtonHandler}
          variant={isError ? 'destructive' : primaryButtonVariant}
        >
          {primaryButtonText}
        </Button>
      </ModalFooter>
    </>
  </Modal>
);

export default ConfirmationModal;
